import type { PriceListBindingResponse, PriceListResponse } from '../model/priceListTypes';
import type { ProductSummary } from '../model/types';

/**
 * The products-list point-of-sale scope (design spec `docs/design/product-card-pricing-spec.md`
 * §8.10, adr-merchant-portal-018 Decision 2) — a SEPARATE concept from the price grid's own
 * `GridScope` (`lib/priceGrid.ts`): the list offers `company | channel | store` (no direct `list`
 * option — a price list is reached only through the store bound to it), and ONE selection drives
 * BOTH `priceScope` and `stockScope` (the BFF's shared `^(channel|store):[A-Za-z0-9_\-]+$`
 * pattern, D1/D2). A separate, price-only customer-group axis composes with every scope and is
 * carried alongside as a plain `string | null`, not part of this type (§8.10).
 */
export type ProductScope =
  | { kind: 'company' }
  | { kind: 'channel'; code: string }
  | { kind: 'store'; code: string };

/** The URL/`Select` sentinel for the default scope — mirrors `lib/priceGrid.ts`'s `BASE_SCOPE`
 * convention (unwritten in the URL; §5/§12: the default "whole company" option stays unwritten). */
export const COMPANY_SCOPE = 'company';

export function encodeProductScope(scope: ProductScope): string {
  return scope.kind === 'company' ? COMPANY_SCOPE : `${scope.kind}:${scope.code}`;
}

export function decodeProductScope(value: string | null): ProductScope {
  if (!value || value === COMPANY_SCOPE) return { kind: 'company' };
  const [kind, ...rest] = value.split(':');
  const code = rest.join(':');
  if (kind === 'channel' && code) return { kind: 'channel', code };
  if (kind === 'store' && code) return { kind: 'store', code };
  return { kind: 'company' };
}

/**
 * The wire value shared, byte-for-byte, by `ProductSearchFilter.priceScope` AND `.stockScope`
 * (D1) — `undefined` (the field stays absent) for the company scope, the BFF's own documented
 * "today's behaviour" sentinel (`docs/design/product-card-pricing-spec.md` §3.2 D1).
 */
export function productScopeWireValue(scope: ProductScope): string | undefined {
  return scope.kind === 'company' ? undefined : `${scope.kind}:${scope.code}`;
}

// ── Provenance (design spec §8.11, adr-merchant-portal-018 Decision 3) ──────────────────────────

/**
 * Which rung of the price-engine ladder actually resolved the row's `price` — derived from
 * `resolve-batch`'s `*Used` codes, passed through the BFF as `ProductSummary.price*Used` (D3). The
 * SAME classification feeds both the price column's provenance caption (§8.11) and the row
 * quick-edit's editor context (§8.12): the number the merchant sees and the slot they edit must
 * always agree ("edit what you see", adr-merchant-portal-018 Decision 3) — one derivation, two
 * consumers, so they cannot drift apart.
 */
export type UsedProvenance =
  | { kind: 'base' }
  | { kind: 'channel'; code: string }
  | { kind: 'channelGroup'; channelCode: string; groupCode: string }
  | { kind: 'group'; code: string }
  | { kind: 'list'; code: string };

/** `undefined`/`null`/`''` all normalise to "absent" here — the same truthiness test `isBaseSlot`
 * (`lib/priceSlots.ts` §8.0) uses for a price LINE's discriminators, so this module's notion of "no
 * code" on a browse-row SUMMARY never drifts from that one. */
type UsedCodes = Pick<
  ProductSummary,
  'priceChannelCodeUsed' | 'priceCustomerGroupCodeUsed' | 'priceListCodeUsed'
>;

/** Table order matches adr-merchant-portal-018 Decision 3 exactly: list beats channel+group beats
 * a plain channel beats a plain group beats base. `priceStatus` is deliberately NOT consulted here
 * — callers gate on it themselves (§8.11: the *Used codes are only meaningful when `ACTIVE`; a
 * caller with `priceStatus !== 'ACTIVE'` should use {@link resolveScopeDeclaredTarget} instead, not
 * this function). */
export function resolveUsedProvenance(product: UsedCodes): UsedProvenance {
  if (product.priceListCodeUsed) return { kind: 'list', code: product.priceListCodeUsed };
  if (product.priceChannelCodeUsed && product.priceCustomerGroupCodeUsed) {
    return {
      kind: 'channelGroup',
      channelCode: product.priceChannelCodeUsed,
      groupCode: product.priceCustomerGroupCodeUsed,
    };
  }
  if (product.priceChannelCodeUsed) return { kind: 'channel', code: product.priceChannelCodeUsed };
  if (product.priceCustomerGroupCodeUsed) return { kind: 'group', code: product.priceCustomerGroupCodeUsed };
  return { kind: 'base' };
}

/**
 * The provenance CAPTION's specific case (§8.11) — like {@link UsedProvenance} but a plain
 * `channel` hit is split into "own" vs "mirrored" (the resolved `channelCodeUsed` differs from the
 * channel the merchant actually requested), a distinction the caption needs (an own-channel label
 * vs a mirror-attribution label) that the edit-CONTEXT table (Decision 3) does not: a mirror edit
 * still targets the effective channel either way, so the editor side never asks this question.
 */
export type ProvenanceCaptionKind = 'base' | 'channel' | 'mirrored' | 'list' | 'group' | 'channelGroup';

export function provenanceCaptionKind(
  provenance: UsedProvenance,
  requestedChannelCode: string | null,
): ProvenanceCaptionKind {
  switch (provenance.kind) {
    case 'list':
      return 'list';
    case 'channelGroup':
      return 'channelGroup';
    case 'group':
      return 'group';
    case 'channel':
      return provenance.code === requestedChannelCode ? 'channel' : 'mirrored';
    case 'base':
    default:
      return 'base';
  }
}

/**
 * §8.11: no caption at all in the plain company scope with no group selected — byte-identical to
 * today. A caption earns its place only once SOME axis (a point of sale, or a customer group) is
 * actually in play, so the merchant always knows why a row's number differs from the plain base
 * price they'd otherwise see.
 */
export function showsProvenanceCaption(scope: ProductScope, groupCode: string | null): boolean {
  return scope.kind !== 'company' || Boolean(groupCode);
}

// ── Scope-declared fallback (adr-merchant-portal-018 Decision 3, reconciliation note 1) ─────────

/**
 * The quick-edit's FALLBACK target when the row carries no `*Used` provenance at all — nothing
 * resolved (`priceStatus !== 'ACTIVE'`), or an older BFF hasn't shipped D3 yet (design spec §8.12
 * item 2; adr-merchant-portal-018 Decision 3 + its reconciliation note 1, which adopts the spec's
 * "scope-declared slot" over the plan's own "client ladder walk" wording — the list holds no
 * per-row price lines, so a client ladder walk would need a per-row fetch §8.14 forbids).
 * Company/channel compose exactly like the provenance-driven table; a `store:` scope additionally
 * needs to know whether the store is bound to an ACTIVE price list — resolved by the caller (which
 * alone holds the bindings fetch), so this module stays pure and synchronous.
 */
export type ScopeDeclaredTarget =
  | { kind: 'base' }
  | { kind: 'channel'; code: string }
  | { kind: 'list'; code: string }
  /** Store scope, but the store carries no ACTIVE list binding — `storeCode` names it so the
   * caller can render the "store isn't bound to a price list" caption (§8.12 item 2,
   * `priceCellEditorDrawer.storeUnbound`). */
  | { kind: 'unboundStoreBase'; storeCode: string };

export function resolveScopeDeclaredTarget(
  scope: ProductScope,
  /** The store's bound ACTIVE list code, if any — `null` when unbound, or when `scope.kind` isn't `'store'`. */
  activeStoreListCode: string | null,
): ScopeDeclaredTarget {
  if (scope.kind === 'channel') return { kind: 'channel', code: scope.code };
  if (scope.kind === 'store') {
    return activeStoreListCode
      ? { kind: 'list', code: activeStoreListCode }
      : { kind: 'unboundStoreBase', storeCode: scope.code };
  }
  return { kind: 'base' };
}

// ── Store→active-list lookup (design spec §8.12/§8.13) ──────────────────────────────────────────

/**
 * The ACTIVE price list `storeCode` is bound to, or `null` — an inactive list's ladder rung is
 * skipped entirely (adr-pricing-025 d4), mirroring `PriceGridPanel`'s own `effectiveScope` guard,
 * so a merchant is never told "this store gets list X" when X can no longer govern anything.
 */
export function resolveActiveStoreListCode(
  storeCode: string,
  lists: PriceListResponse[],
  bindingsByCode: Map<string, PriceListBindingResponse[]>,
): string | null {
  for (const [listCode, bindings] of bindingsByCode.entries()) {
    if (!bindings.some((binding) => binding.storeCode === storeCode)) continue;
    const list = lists.find((candidate) => candidate.code === listCode);
    if (list?.active) return listCode;
  }
  return null;
}
